import { DateTimeFormat, formatNumberCommaSeparated, formatPercentage, getLocalDateTime } from '../../../../../../common';
import { formatDollars } from '../../../../../../common/utils/currencyFunctions';
import {
  AssetGroup,
  AssetGroupItem,
  MatchedSecurityTableFields,
  PortfolioDetailResult,
  PortfolioParameters,
  SecurityColumnVisibility,
  UnrealisedGainsLossesDetail,
  UnrealisedGainsLossesGroup,
} from '../store/types';

type CsvRowBuilder = string[];

export function isUnrealisedGainsLossesGroup(result: MatchedSecurityTableFields | undefined): result is UnrealisedGainsLossesGroup {
  if (result === undefined) return false;
  return 'items' in result && Array.isArray((result as UnrealisedGainsLossesGroup).items);
}

export function getSecurityTableFields(
  row: AssetGroup | null,
  portfolioDetailResult: PortfolioDetailResult | undefined
): MatchedSecurityTableFields | undefined {
  if (!row) return undefined;

  const groups = portfolioDetailResult?.gainsLossesDetail?.unrealisedGainsLossesGroup ?? [];
  const rowName = (row.securityName ?? row.groupName ?? '').toLowerCase();
  const rowCode = (row.securityCode ?? '').toLowerCase();

  for (const group of groups) {
    const matchesGroup =
      (rowName === (group.groupName ?? '').toLowerCase() || rowName === (group.subGroupName ?? '').toLowerCase()) &&
      row.items.length === group.items.length;

    if (matchesGroup) {
      if (group.items.length === 0 || row.items?.length === 0) {
        return group;
      }
      const hasMatchingItem = group.items.some((item) =>
        row.items?.some(
          (rowItem) =>
            (item.groupName ?? '').toLowerCase() === (rowItem.securityName ?? '').toLowerCase() ||
            (item.groupSubName ?? '').toLowerCase().includes((rowItem.securityCode ?? '').toLowerCase())
        )
      );
      if (hasMatchingItem) {
        return group;
      }
    }

    const matchedItem = group.items.find(
      (item) =>
        (item.groupName ?? '').toLowerCase() === rowName ||
        (item.groupSubName ?? '').toLowerCase().includes(rowName) ||
        (rowCode && (item.groupSubName ?? '').toLowerCase().includes(rowCode))
    );

    if (matchedItem) {
      return matchedItem;
    }
  }
  return undefined;
}

export function findMatchedItem(item: AssetGroupItem, parentGroup: UnrealisedGainsLossesGroup | undefined): UnrealisedGainsLossesDetail | undefined {
  return parentGroup?.items.find((groupItem) => {
    const nameMatches = item.securityName && (groupItem.groupName ?? '').toLowerCase() === item.securityName.toLowerCase();
    const codeMatches = item.securityCode && (groupItem.groupSubName ?? '').toLowerCase().includes(item.securityCode.toLowerCase());
    return nameMatches && codeMatches;
  });
}

function buildHeaders(columnVisibility: SecurityColumnVisibility): string[] {
  const headers: string[] = ['Group Name', 'Security Name'];
  const columnMapping: Array<[keyof SecurityColumnVisibility, string]> = [
    ['securityCode', 'Security Code'],
    ['units', 'Units'],
    ['costBase', 'Cost Base'],
    ['unitCost', 'Avg Unit Cost'],
    ['unitPrice', 'Price (AUD)'],
    ['nativePrice', 'Price (Native)'],
    ['currentValue', 'Value (AUD)'],
    ['nativeValue', 'Value (Native)'],
    ['currentWeightPercentage', 'Port. %'],
    ['returnSinceInception', 'Return Since Inception'],
    ['returnSinceInceptionPercentage', 'Return Since Inception %'],
    ['gainLossValue', 'Unrealised Gain/Loss'],
    ['gainLossPercentage', 'Unrealised Gain/Loss %'],
    ['estimatedIncome', 'Est. Income'],
    ['estimatedYield', 'Est. Yield'],
    ['estimatedGrossIncome', 'Est. Gross Income'],
    ['estimatedGrossYield', 'Est. Gross Yield'],
    ['targetValue', 'Target Value'],
    ['targetWeight', 'Target Weight'],
    ['differenceValue', 'Value Difference'],
    ['differenceWeightPercentage', 'Weight Difference'],
    ['unsettledUnits', 'Unsettled Units'],
    ['unsettledValue', 'Unsettled Value'],
    ['priceDate', 'Price Date'],
    ['currencyCode', 'Native Currency'],
    ['forexRate', 'Forex Rate'],
  ];

  columnMapping.forEach(([key, label]) => {
    if (columnVisibility[key]) headers.push(`"${label}"`);
  });

  return headers;
}

function buildChildRow(
  item: AssetGroupItem,
  matched: UnrealisedGainsLossesDetail | undefined,
  parameters: PortfolioParameters,
  columnVisibility: SecurityColumnVisibility,
  groupName: string
): CsvRowBuilder {
  const childRow: CsvRowBuilder = [`"${groupName}"`, `"${item.securityName || ''}"`];

  const addCell = (key: keyof SecurityColumnVisibility, value: string) => {
    if (columnVisibility[key]) childRow.push(`"${value}"`);
  };
  addCell('securityCode', item.securityCode || '');
  addCell('units', formatNumberCommaSeparated(item.units, parameters.unitDecimalPlaces));
  addCell('costBase', formatDollars(matched?.costBase ?? 0));
  addCell('unitCost', formatDollars(matched?.unitCost ?? 0, false, 4));
  addCell('unitPrice', formatDollars(item.unitPrice, false, 4));
  addCell('nativePrice', formatDollars(item.nativePrice, false, 4));
  addCell('currentValue', formatDollars(item.currentValue));
  addCell('nativeValue', formatDollars(item.nativeValue));
  addCell('currentWeightPercentage', formatPercentage(item.currentWeightPercentage));
  addCell('returnSinceInception', formatDollars(item.returnSinceInception ?? 0));
  addCell('returnSinceInceptionPercentage', !item.returnSinceInceptionPercentage ? '-' : formatPercentage(item.returnSinceInceptionPercentage));
  addCell('gainLossValue', formatDollars(matched?.gainLossValue ?? 0));
  addCell('gainLossPercentage', formatPercentage(matched?.gainLossPercentage ?? 0));
  addCell('estimatedIncome', formatDollars(item.estimatedIncome));
  addCell('estimatedYield', formatPercentage(item.estimatedYield));
  addCell('estimatedGrossIncome', formatDollars(item.estimatedGrossIncome));
  addCell('estimatedGrossYield', formatPercentage(item.estimatedGrossYield));
  addCell('targetValue', formatDollars(item.targetValue));
  addCell('targetWeight', formatPercentage(item.targetWeightPercentage));
  addCell('differenceValue', formatDollars(item.targetValue));
  addCell('differenceWeightPercentage', formatPercentage(item.differenceWeightPercentage));
  addCell('unsettledUnits', `${item.unsettledUnits || 0}`);
  addCell('unsettledValue', formatDollars(item.unsettledValue));
  addCell('priceDate', getLocalDateTime(item.priceDate, DateTimeFormat.Short) || '');
  addCell('currencyCode', item.currencyCode || '');
  addCell('forexRate', formatNumberCommaSeparated(item.forexRate, 6));
  return childRow;
}

function buildGrandTotalRow(portfolioDetailResult: PortfolioDetailResult | undefined, columnVisibility: SecurityColumnVisibility): CsvRowBuilder {
  const grandTotalRow: CsvRowBuilder = ['"Grand Total"', ''];

  const addCell = (key: keyof SecurityColumnVisibility, value: string) => {
    if (columnVisibility[key]) grandTotalRow.push(`"${value}"`);
  };

  const totalReturnSinceInception = (portfolioDetailResult?.portfolioDetail?.items ?? []).reduce(
    (total, group) => total + (group.items?.reduce((acc, item) => acc + (item.returnSinceInception ?? 0), 0) ?? group.returnSinceInception ?? 0),
    0
  );
  addCell('securityCode', '');
  addCell('units', '');
  addCell('costBase', formatDollars(portfolioDetailResult?.gainsLossesDetail?.totalCostBase ?? 0));
  addCell('unitCost', '');
  addCell('unitPrice', '');
  addCell('nativePrice', '');
  addCell('currentValue', formatDollars(portfolioDetailResult?.portfolioDetail?.marketValue ?? 0));
  addCell('nativeValue', '');
  addCell('currentWeightPercentage', '100%');
  addCell('returnSinceInception', formatDollars(totalReturnSinceInception));
  addCell(
    'returnSinceInceptionPercentage',
    !portfolioDetailResult?.portfolioDetail?.totalReturnSinceInceptionPercentage
      ? '-'
      : formatPercentage(portfolioDetailResult?.portfolioDetail?.totalReturnSinceInceptionPercentage)
  );
  addCell('gainLossValue', formatDollars(portfolioDetailResult?.gainsLossesDetail?.totalUnrealisedGainLoss ?? 0));
  addCell('gainLossPercentage', formatPercentage(portfolioDetailResult?.gainsLossesDetail?.gainLossPercentage ?? 0));
  addCell('estimatedIncome', formatDollars(portfolioDetailResult?.portfolioDetail?.totalEstimatedIncome ?? 0));
  addCell('estimatedYield', formatPercentage(portfolioDetailResult?.portfolioDetail?.totalEstimatedYield ?? 0));
  addCell('estimatedGrossIncome', formatDollars(portfolioDetailResult?.portfolioDetail?.totalEstimatedGrossIncome ?? 0));
  addCell('estimatedGrossYield', formatPercentage(portfolioDetailResult?.portfolioDetail?.totalEstimatedGrossYield ?? 0));
  addCell('targetValue', '');
  addCell('targetWeight', '');
  addCell('differenceValue', '');
  addCell('differenceWeightPercentage', '');
  addCell('unsettledUnits', '');
  addCell('unsettledValue', '');
  addCell('priceDate', '');
  addCell('currencyCode', '');
  addCell('forexRate', '');
  return grandTotalRow;
}

export function generateDetailedCsv(
  portfolioDetailResult: PortfolioDetailResult | undefined,
  parameters: PortfolioParameters,
  columnVisibility: SecurityColumnVisibility
): string {
  if (!portfolioDetailResult?.portfolioDetail?.items) {
    return '';
  }

  const rows = portfolioDetailResult.portfolioDetail.items;
  const csvRows: string[] = [buildHeaders(columnVisibility).join(',')];

  rows.forEach((row: AssetGroup) => {
    const fields = getSecurityTableFields(row, portfolioDetailResult);
    const parentGroup = isUnrealisedGainsLossesGroup(fields) ? fields : undefined;
    const groupName = row?.groupName ?? '';

    if (row?.items && row.items.length > 0) {
      row.items.forEach((item: AssetGroupItem) => {
        const matched = findMatchedItem(item, parentGroup);
        csvRows.push(buildChildRow(item, matched, parameters, columnVisibility, groupName).join(','));
      });
    }
  });

  csvRows.push(buildGrandTotalRow(portfolioDetailResult, columnVisibility).join(','));
  return csvRows.join('\n');
}
